import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Target, TrendingUp, LineChart, Infinity as InfinityIcon, Check, ArrowRight, ArrowUpRight } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { CTABand } from "@/components/site/CTABand";
import { Reveal, RevealGroup, RevealItem } from "@/components/site/Reveal";
import { SectionHeading } from "@/components/site/Primitives";
import { BookingDialog } from "@/components/site/BookingDialog";
import { SERVICES, IMAGES } from "@/lib/content";
import api from "@/lib/api";

const ICONS = { Target, TrendingUp, LineChart, Infinity: InfinityIcon };
const HERO_IMG = { recruitment: IMAGES.boardroom, "talent-development": IMAGES.office, consulting: IMAGES.architecture, "beyond-partnership": IMAGES.marble };

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    api.getCaseStudies().then((all) => {
      const map = { recruitment: "Recruitment", "talent-development": "Talent Development", consulting: "Consulting", "beyond-partnership": null };
      const svc = map[slug];
      setRelated(svc ? all.filter((c) => c.service === svc).slice(0, 3) : all.slice(0, 3));
    }).catch(() => {});
  }, [slug]);

  if (!service) return <Navigate to="/services" replace />;
  const Icon = ICONS[service.icon] || Target;

  return (
    <div data-testid="service-detail-page">
      <PageHero
        breadcrumb={[{ label: "Services", to: "/services" }, { label: service.short }]}
        eyebrow={service.short}
        title={service.tagline}
        intro={service.summary}
        image={HERO_IMG[slug]}
        accent={service.premium ? "gold" : "teal"}
      >
        <BookingDialog trigger={
          <button className="group inline-flex items-center gap-2 rounded-full bg-[color:var(--bp-teal)] px-7 py-3.5 text-sm font-semibold text-[color:var(--bp-bg)] shadow-[var(--shadow-soft)] transition-shadow hover:shadow-[var(--shadow-lift)]">
            Discuss your {service.short.toLowerCase()} needs <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
        } />
      </PageHero>

      <section className="section-py">
        <div className="container-bp grid grid-cols-1 gap-14 lg:grid-cols-[0.85fr_1.15fr]">
          <div>
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-[color:var(--bp-teal)]/40 bg-[color:var(--bp-teal-soft)] text-[color:var(--bp-teal)]"><Icon className="h-6 w-6" /></span>
            <SectionHeading className="mt-6" eyebrow="What you get" title={service.name} />
          </div>
          <RevealGroup className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {service.points.map((p) => (
              <RevealItem key={p}>
                <div className="flex h-full items-start gap-3 rounded-[var(--radius-md)] border border-[color:var(--bp-border)] bg-[color:var(--bp-surface)] p-5">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-[color:var(--bp-teal)]" />
                  <p className="text-[14.5px] leading-relaxed text-[color:var(--bp-cream)]">{p}</p>
                </div>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {related.length > 0 && (
        <section className="section-py bg-[color:var(--bp-bg-2)]">
          <div className="container-bp">
            <div className="flex items-end justify-between">
              <SectionHeading eyebrow="Proof" title="Related mandates." />
              <Link to="/case-studies" className="hidden items-center gap-1.5 text-[13px] font-semibold text-[color:var(--bp-teal)] sm:inline-flex">All case studies <ArrowUpRight className="h-4 w-4" /></Link>
            </div>
            <RevealGroup className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
              {related.map((c) => (
                <RevealItem key={c.slug}>
                  <Link to={`/case-studies/${c.slug}`} className="group flex h-full flex-col overflow-hidden rounded-[var(--radius-lg)] border border-[color:var(--bp-border)] bg-[color:var(--bp-surface)] transition-all duration-300 hover:-translate-y-1 hover:border-[color:var(--bp-teal)]/50">
                    <div className="aspect-[16/10] overflow-hidden"><img src={c.image} alt={c.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
                    <div className="flex flex-1 flex-col p-6">
                      <h3 className="font-display text-lg font-semibold text-[color:var(--bp-cream)]">{c.title}</h3>
                      <p className="mt-2 flex-1 text-[13.5px] text-[color:var(--bp-text-muted)]">{c.summary}</p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[color:var(--bp-teal)]">Read case study <ArrowRight className="h-4 w-4" /></span>
                    </div>
                  </Link>
                </RevealItem>
              ))}
            </RevealGroup>
          </div>
        </section>
      )}

      <CTABand title={`Ready to talk ${service.short.toLowerCase()}?`} />
    </div>
  );
}
